import { useContext } from 'react'
import { v4 as uuid } from 'uuid'

import { isPopularDestination } from '@awaze/design-system.utils.form'
import { useIntlContext } from '@awaze/mfe-common.contexts.intl'
import {
  Dispatch,
  FILTER_ACCOMMODATION_TYPE_TOGGLE,
  FILTER_BATHROOM_TOGGLE,
  FILTER_BEDROOM_TOGGLE,
  FILTER_FEATURE_TOGGLE,
  FILTER_GRADE_TOGGLE,
  FILTER_RATING_TOGGLE,
  FILTER_REMOVE_MIN_OR_MAX_PRICE,
  FILTER_SITE_FEATURE_TOGGLE,
  ONLY_DISCOUNTED_TOGGLE,
  REMOVE_POPULAR_DESTINATION,
} from '@awaze/mfe-common.contexts.search'
import { TmsContext } from '@awaze/mfe-common.contexts.tms'
import type {
  ICommonAggregations,
  SearchCriteria,
} from '@awaze/mfe-common.types-and-mocks.site-search-service'
import {
  AccordionCollapse,
  AccordionItem,
  AccordionToggle,
} from '@awazevr/design-system.components.accordion'
import { Chip } from '@awazevr/design-system.components.chip'

import { mapGradeParamToNumber } from '@/utils/helpers'

import { handleChipClick, handleClearAllClick } from './FilterActions/handlers'
import { FilterGroupLabels } from './Filters.types'
import {
  bathroomMapper,
  bedroomMapper,
  getFeatureGroupLabel,
  isAccommodationFeature,
  isSiteFeature,
} from './Filters.utils'

import styles from './Filters.module.scss'

interface SelectedFiltersProps {
  accommodationTypesAsFeatures: ICommonAggregations['features']
  filterGroupLabels: FilterGroupLabels
  popularDestinationIds: Array<string>
  popularFeatureIds: Array<string>
  searchCriteria: SearchCriteria
  selectedFilters: ICommonAggregations['features']
  siteFeatures: ICommonAggregations['features']
  totalFiltersSelectedCount: number
  dispatch: Dispatch
}

export const SelectedFilters = ({
  accommodationTypesAsFeatures,
  filterGroupLabels,
  popularDestinationIds,
  popularFeatureIds,
  searchCriteria,
  selectedFilters,
  siteFeatures,
  totalFiltersSelectedCount,
  dispatch,
}: SelectedFiltersProps): JSX.Element | null => {
  const { bathrooms, bedrooms, grade, rating, minPrice, maxPrice, discounts } =
    searchCriteria

  const tms = useContext(TmsContext)
  const { formatCurrency, formatMessage } = useIntlContext()

  const showBathrooms = bathrooms && bathrooms.length > 0
  const showBedrooms = bedrooms && bedrooms.length > 0
  const showClearAllChip = totalFiltersSelectedCount > 2
  const showGrade = grade && grade.length > 0
  const showMaxPrice = maxPrice && maxPrice > 0
  const showMinPrice = minPrice && minPrice > 0
  const showRating = rating && rating.length > 0
  const showDiscount = Boolean(discounts)
  const shouldShowSelectedFilters =
    totalFiltersSelectedCount > 0 || showDiscount

  const handleChipClickWithDefaultArgs = (clickedFeature: {
    featureName: string
    featureGroup?: string
  }) =>
    handleChipClick({
      tms,
      clickedFeature: clickedFeature,
    })

  return shouldShowSelectedFilters ? (
    <AccordionItem disabled>
      <AccordionToggle>
        {formatMessage({
          id: 'selected-filters.selected-filter.title',
          defaultMessage: 'Selected filters',
        })}
      </AccordionToggle>
      <AccordionCollapse
        aria-label={formatMessage({
          id: 'selected-filters.selected-filter.aria-label',
          defaultMessage: 'Selected filters list',
        })}
      >
        <div className={styles.filterList}>
          {selectedFilters.map((selectedFilter) => (
            <Chip
              key={`${selectedFilter.featureId}-${uuid()}`}
              text={selectedFilter.featureName || ''}
              ariaLabel={selectedFilter.featureName || ''}
              variant="medium"
              onClick={() => {
                const siteFeature = isSiteFeature(selectedFilter, siteFeatures)
                const accommodationFeature = isAccommodationFeature(
                  selectedFilter,
                  accommodationTypesAsFeatures,
                )
                const featureGroup = getFeatureGroupLabel({
                  featureId: selectedFilter.featureId
                    ? String(selectedFilter.featureId)
                    : null,
                  filterGroupLabels,
                  popularDestinationIds,
                  popularFeatureIds,
                })
                handleChipClickWithDefaultArgs({
                  featureName: selectedFilter.featureName || '',
                  featureGroup,
                })
                if (accommodationFeature) {
                  dispatch({
                    type: FILTER_ACCOMMODATION_TYPE_TOGGLE,
                    accommodationTypeId: selectedFilter.featureId,
                  })
                } else if (
                  isPopularDestination(selectedFilter.featureId) &&
                  !siteFeature
                ) {
                  dispatch({
                    type: REMOVE_POPULAR_DESTINATION,
                    featureId: selectedFilter.featureId,
                  })
                } else if (siteFeature) {
                  dispatch({
                    type: FILTER_SITE_FEATURE_TOGGLE,
                    siteFeatureId: selectedFilter.featureId,
                  })
                } else {
                  dispatch({
                    type: FILTER_FEATURE_TOGGLE,
                    featureId: selectedFilter.featureId,
                  })
                }
              }}
            />
          ))}
          {showBathrooms && (
            <Chip
              text={formatMessage(
                {
                  id: 'selected-filters.bathrooms.text',
                  defaultMessage: '{bathroomMapper}+ bathrooms',
                },
                { bathroomMapper: bathroomMapper(bathrooms) },
              )}
              ariaLabel={formatMessage(
                {
                  id: 'selected-filters.bathrooms.ariaLabel',
                  defaultMessage: '{bathroomMapper}+ bathrooms',
                },
                { bathroomMapper: bathroomMapper(bathrooms) },
              )}
              variant="medium"
              onClick={() => {
                dispatch({
                  type: FILTER_BATHROOM_TOGGLE,
                  id: bathrooms,
                })
                handleChipClickWithDefaultArgs({
                  featureName: `${bathroomMapper(bathrooms)}+ bathrooms`,
                  featureGroup: filterGroupLabels.bathrooms,
                })
              }}
            />
          )}
          {showBedrooms && (
            <Chip
              text={formatMessage(
                {
                  id: 'selected-filters-bedrooms.text',
                  defaultMessage: '{bedroomMapper}+ bedrooms',
                },
                { bedroomMapper: bedroomMapper(bedrooms) },
              )}
              ariaLabel={formatMessage(
                {
                  id: 'selected-filters-bedrooms.ariaLabel',
                  defaultMessage: '{bedroomMapper}+ bedrooms',
                },
                { bedroomMapper: bedroomMapper(bedrooms) },
              )}
              variant="medium"
              onClick={() => {
                dispatch({
                  type: FILTER_BEDROOM_TOGGLE,
                  id: bedrooms,
                })
                handleChipClickWithDefaultArgs({
                  featureName: `${bedroomMapper(bedrooms)}+ bedrooms`,
                  featureGroup: filterGroupLabels.bedrooms,
                })
              }}
            />
          )}
          {showMinPrice && (
            <Chip
              text={formatMessage(
                {
                  id: 'selected-filters.from-text',
                  defaultMessage: 'From: {minPrice}',
                },
                {
                  minPrice: formatCurrency(minPrice),
                },
              )}
              ariaLabel={formatMessage(
                {
                  id: 'selected-filters.from-ariaLabel',
                  defaultMessage: 'From: {minPrice}',
                },
                {
                  minPrice: formatCurrency(minPrice),
                },
              )}
              variant="medium"
              onClick={() => {
                dispatch({
                  type: FILTER_REMOVE_MIN_OR_MAX_PRICE,
                  field: 'minPrice',
                })
                handleChipClickWithDefaultArgs({
                  featureName: formatMessage(
                    {
                      id: 'selected-filters.from-ariaLabel',
                      defaultMessage: 'From: {minPrice}',
                    },
                    {
                      minPrice: formatCurrency(minPrice),
                    },
                  ),
                })
              }}
            />
          )}
          {showMaxPrice && (
            <Chip
              text={formatMessage(
                {
                  id: 'selected-filters.to-text',
                  defaultMessage: 'To: {maxPrice}',
                },
                {
                  maxPrice: formatCurrency(maxPrice),
                },
              )}
              ariaLabel={formatMessage(
                {
                  id: 'selected-filters.to-ariaLabel',
                  defaultMessage: 'To: {maxPrice}',
                },
                {
                  maxPrice: formatCurrency(maxPrice),
                },
              )}
              variant="medium"
              onClick={() => {
                dispatch({
                  type: FILTER_REMOVE_MIN_OR_MAX_PRICE,
                  field: 'maxPrice',
                })
                handleChipClickWithDefaultArgs({
                  featureName: formatMessage(
                    {
                      id: 'selected-filters.to-text',
                      defaultMessage: 'To: {maxPrice}',
                    },
                    {
                      maxPrice: formatCurrency(maxPrice),
                    },
                  ),
                })
              }}
            />
          )}
          {showGrade && (
            <Chip
              text={formatMessage(
                {
                  id: 'selected-filters.stars',
                  defaultMessage: '{mapGradeParamToNumber} stars',
                },
                { mapGradeParamToNumber: mapGradeParamToNumber(grade) },
              )}
              ariaLabel={formatMessage(
                {
                  id: 'selected-filters.stars.ariaLabel',
                  defaultMessage: '{mapGradeParamToNumber} stars',
                },
                { mapGradeParamToNumber: mapGradeParamToNumber(grade) },
              )}
              variant="medium"
              onClick={() => {
                dispatch({
                  type: FILTER_GRADE_TOGGLE,
                  id: grade,
                })
                handleChipClickWithDefaultArgs({
                  featureName: `${mapGradeParamToNumber(grade)} stars`,
                  featureGroup: filterGroupLabels.starRating,
                })
              }}
            />
          )}
          {showRating && (
            <Chip
              text={formatMessage(
                {
                  id: 'selected-filters.customer-review',
                  defaultMessage: 'Customer reviews: {rating}',
                },
                { rating },
              )}
              ariaLabel={formatMessage(
                {
                  id: 'selected-filters.customer-reviews.ariaLabel',
                  defaultMessage: 'Customer reviews: {rating}',
                },
                { rating },
              )}
              variant="medium"
              onClick={() => {
                dispatch({
                  type: FILTER_RATING_TOGGLE,
                  id: rating,
                })
                handleChipClickWithDefaultArgs({
                  featureName: `Customer reviews: ${rating}`,
                  featureGroup: filterGroupLabels.customerReviewScore,
                })
              }}
            />
          )}

          {showDiscount && (
            <Chip
              text={formatMessage({
                id: 'filters.extras.deals',
                defaultMessage: 'Discount',
              })}
              ariaLabel={formatMessage({
                id: 'filters.extras.deals',
                defaultMessage: 'Discount',
              })}
              variant="medium"
              onClick={() => {
                dispatch({
                  type: ONLY_DISCOUNTED_TOGGLE,
                })
              }}
            />
          )}

          {showClearAllChip && (
            <Chip
              text={formatMessage({
                id: 'selected-filters.clear-all.text',
                defaultMessage: 'Clear all',
              })}
              ariaLabel={formatMessage({
                id: 'selected-filters.clear-all.ariaLabel',
                defaultMessage: 'Clear all',
              })}
              variant="low"
              onClick={() => {
                handleClearAllClick({
                  dispatch,
                  tms,
                })
              }}
            />
          )}
        </div>
      </AccordionCollapse>
    </AccordionItem>
  ) : null
}
